import { Button, Form, Spinner } from 'react-bootstrap';
import { CustomerAffiliationCsv, VCOAffiliation, VCOProject } from './CustomProjectModels';
import { HmisProject, SelectOption } from '../../../Shared/SharedModels';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import { AddFilled } from '@carbon/icons-react';
import ApiHelper from '../../../Shared/ApiHelper';
import DeleteButton from '../../../Shared/components/DeleteButton';
import SaveButton from '../../../Shared/components/SaveButton';
import Select from 'react-select';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface AffiliationEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

function getAffiliationOptionLabel(csv: CustomerAffiliationCsv): string {
    return csv.affiliationID ? `Affiliation ID: ${csv.affiliationID}` : 'New Affiliation';
}

function getProjectOptionLabel(project: HmisProject): string {
    return project.projectName + ': ' + project.projectId;
}

const AffiliationEditForm: FunctionComponent<AffiliationEditFormProps> = (props) => {
    const { vcoProject, batchKey } = props;
    const userProfile = useContext(UserProfileContext);

    const defaultAffiliationCsv = useMemo<CustomerAffiliationCsv>(
        () => ({
            batchKey: 0,
            sourceSystemShortName: '',
            projectID: '',
            resProjectID: '',
        }),
        []
    );

    const [selectedAffiliation, setSelectedAffiliation] = useState<CustomerAffiliationCsv | undefined>();
    const [affiliationOptions, setAffiliationOptions] = useState<SelectOption<CustomerAffiliationCsv>[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [canSave, setCanSave] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    const [projectOptions, setProjectOptions] = useState<SelectOption<HmisProject>[]>([]);
    const [selectedProject, setSelectedProject] = useState<HmisProject>();
    const [areProjectsLoading, setAreProjectsLoading] = useState(true);

    useEffect(() => {
        setAreProjectsLoading(true);

        api.callApi(
            instance,
            [process.env.REACT_APP_B2C_SCOPE ?? ''],
            process.env.REACT_APP_NET_API_URL + '/CustomProject/projects'
        )
            .then(async (result: Response) => {
                const hmisProjects: HmisProject[] = await result.json();

                setProjectOptions(
                    hmisProjects
                        .filter((proj) => proj.sourceSystem?.sourceSystemKey === vcoProject.sourceSystemKey)
                        .map((proj) => {
                            return { label: getProjectOptionLabel(proj), value: proj };
                        })
                );
            })
            .finally(() => {
                setAreProjectsLoading(false);
            });
    }, [api, instance, vcoProject.sourceSystemKey]);

    useEffect(() => {
        if (projectOptions.length && selectedAffiliation) {
            setSelectedProject(
                projectOptions.find((option) => option.value.projectId === selectedAffiliation?.resProjectID)?.value ??
                    undefined
            );
        }
    }, [projectOptions, projectOptions.length, selectedAffiliation]);

    useEffect(() => {
        setCanSave(!!selectedAffiliation?.resProjectID);
    }, [selectedAffiliation?.resProjectID]);

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                `${process.env.REACT_APP_NET_API_URL}/CustomProject/projects/${vcoProject.projectKey}/affiliation`,
                'GET'
            )
                .then(async (result: Response) => {
                    const affiliations: VCOAffiliation[] = await result.json();
                    const customAffiliations: CustomerAffiliationCsv[] = affiliations.map<CustomerAffiliationCsv>(
                        (vco) => ({
                            batchKey: batchKey,
                            affiliationID: vco.affiliationID,
                            sourceSystemShortName:
                                userProfile.sourceSystems.find((s) => s.sourceSystemKey === vco.sourceSystemKey)
                                    ?.sourceSystemShortName ?? '',
                            projectID: vco.projectID ?? '',
                            resProjectID: vco.resProjectID ?? '',
                            dateCreated: vco.dateCreated,
                            dateUpdated: vco.dateUpdated,
                            userID: vco.userID,
                            dateDeleted: vco.dateDeleted,
                        })
                    );

                    if (customAffiliations.length) {
                        setAffiliationOptions(
                            customAffiliations.map((csv) => ({
                                label: getAffiliationOptionLabel(csv),
                                value: csv,
                            }))
                        );
                        setSelectedAffiliation(customAffiliations[0]);
                    } else {
                        setAffiliationOptions([]);
                        setSelectedAffiliation(undefined);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [api, batchKey, instance, isLoading, userProfile.sourceSystems, vcoProject.projectKey]);

    return (
        <div className="project-card mb-5">
            <div className="project-card-header">
                <h3>Affiliation CSV Data</h3>

                {!!batchKey && !isLoading && (
                    <div className="flex-row">
                        {selectedAffiliation?.affiliationID && (
                            <DeleteButton
                                isLoading={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const dateDeleted = new Date().toISOString();

                                    const payload: CustomerAffiliationCsv = {
                                        ...selectedAffiliation,
                                        dateDeleted: dateDeleted,
                                        dateUpdated: dateDeleted,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        `${process.env.REACT_APP_NET_API_URL}/CustomProject/affiliation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Affiliation Data Deleted Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}

                        <Button
                            className="form-action-button mr-2 ml-2"
                            variant="outline-primary"
                            disabled={isSaving}
                            onClick={() => {
                                const currentDate = new Date().toISOString().split('T')[0];
                                const newAffiliation: CustomerAffiliationCsv = {
                                    batchKey: batchKey,
                                    sourceSystemShortName:
                                        userProfile.sourceSystems.find(
                                            (s) => s.sourceSystemKey === vcoProject.sourceSystemKey
                                        )?.sourceSystemShortName ?? '',
                                    projectID: vcoProject.projectId ?? '',
                                    resProjectID: '',
                                    dateCreated: currentDate,
                                    dateUpdated: currentDate,
                                    userID: vcoProject.userID,
                                };

                                setSelectedAffiliation(newAffiliation);
                                setSelectedProject(undefined);
                            }}
                        >
                            <AddFilled size={24} className="button-icon-padding" /> Add Affiliation
                        </Button>

                        {selectedAffiliation && (
                            <SaveButton
                                isSaving={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const payload: CustomerAffiliationCsv = {
                                        ...selectedAffiliation,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        `${process.env.REACT_APP_NET_API_URL}/CustomProject/affiliation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Affiliation Data Saved Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <Spinner
                    className="centered-spinner"
                    variant="primary"
                    size="sm"
                    animation="border"
                    data-testid="affiliationEditFormSpinner"
                />
            ) : (
                <div className="project-card-body">
                    {affiliationOptions.length || selectedAffiliation ? (
                        <>
                            <Form.Group className="wide-card-form-group">
                                <Form.Label className={isSaving ? 'disabled-label' : ''}>Affiliation</Form.Label>
                                <Select
                                    isClearable
                                    isLoading={isLoading}
                                    isDisabled={isSaving}
                                    className="filterSelect"
                                    placeholder="Select Affiliation..."
                                    value={
                                        selectedAffiliation
                                            ? {
                                                  label: getAffiliationOptionLabel(selectedAffiliation),
                                                  value: selectedAffiliation,
                                              }
                                            : undefined
                                    }
                                    options={affiliationOptions}
                                    onChange={(selectedOption) => {
                                        setSelectedAffiliation(selectedOption?.value);
                                    }}
                                />
                            </Form.Group>

                            <Form className="card-form">
                                {selectedAffiliation && (
                                    <Form.Group className="wide-card-form-group">
                                        <Form.Label>Res Project ID*</Form.Label>
                                        <Select
                                            isClearable
                                            data-testid="resProjectIDInput"
                                            isLoading={areProjectsLoading}
                                            className="filterSelect"
                                            placeholder="Select Affiliation..."
                                            menuPlacement="top"
                                            value={
                                                selectedProject
                                                    ? {
                                                          label: getProjectOptionLabel(selectedProject),
                                                          value: selectedProject,
                                                      }
                                                    : undefined
                                            }
                                            options={projectOptions}
                                            onChange={(selectedOption) => {
                                                setSelectedProject(selectedOption?.value);

                                                if (selectedOption?.value) {
                                                    setSelectedAffiliation((prev) => ({
                                                        ...defaultAffiliationCsv,
                                                        ...prev,
                                                        resProjectID: selectedOption.value.projectId,
                                                    }));
                                                }
                                            }}
                                        />
                                    </Form.Group>
                                )}
                            </Form>
                        </>
                    ) : (
                        <p>No affiliation records found</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AffiliationEditForm;
